import React from "react";
import FilterForm from "../FilterForm";
import Alert from 'react-bootstrap/Alert';
import "./css/header.css";
import OpenCurrency from "../quick-actions/OpenCurrency";

function timeAgo(lastUpdatedTs) {
  if (!lastUpdatedTs || isNaN(lastUpdatedTs)) {
    return "Invalid timestamp";
  }
  const now = new Date();
  const eventDate = new Date(lastUpdatedTs * 1000);
  const diff = now - eventDate;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return `${seconds} sec ago`;
  } else if (minutes < 60) {
    return `${minutes} min ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else {
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }
}
const wrapHeader = {
  display: "flex",
  flexDirection: "column",
};

const HOURLY_VERDICTS = new Set(["BULLISH", "BEARISH", "NEUTRAL"]);

function verdictLabel(verdict) {
  const normalized = String(verdict || "").toUpperCase();
  return normalized ? normalized.charAt(0) + normalized.slice(1).toLowerCase() : "";
}

function getRecentHourlyRegimes(history = []) {
  const seenHours = new Set();
  const hourlyReports = history.filter((report) => {
    const timestamp = Date.parse(report?.asOf);
    return (report?.horizonDays || 1) === 1 && Number.isFinite(timestamp);
  }).sort((a, b) => Date.parse(b.asOf) - Date.parse(a.asOf)).filter((report) => {
    const timestamp = Date.parse(report.asOf);

    const hour = Math.floor(timestamp / 3600000);
    if (seenHours.has(hour)) return false;
    seenHours.add(hour);
    return true;
  });

  const regimes = [];
  let previousHour = null;
  let missingHours = 0;
  for (const report of hourlyReports) {
    const verdict = String(report.verdict || "").toUpperCase();
    if (!HOURLY_VERDICTS.has(verdict)) break;
    const latestRegime = regimes[regimes.length - 1];
    if (latestRegime?.verdict !== verdict && regimes.length === 2) break;
    const hour = Math.floor(Date.parse(report.asOf) / 3600000);
    if (previousHour !== null) missingHours += Math.max(0, previousHour - hour - 1);
    previousHour = hour;
    if (latestRegime?.verdict === verdict) {
      latestRegime.hours += 1;
      latestRegime.from = report.asOf;
    } else if (regimes.length < 2) {
      regimes.push({ verdict, hours: 1, from: report.asOf, through: report.asOf });
    }
  }

  return { regimes: regimes.reverse(), missingHours };
}

function HourlyRegime({ regime }) {
  const className = `direction-hourly-state direction-hourly-${regime.verdict.toLowerCase()}`;
  return <span className={className} title={`${regime.hours} reported hourly verdicts; ${regime.from} to ${regime.through}`}>{regime.hours}h {verdictLabel(regime.verdict)}</span>;
}

function TopSection({ lastUpdatedTs, handleRefresh, sendFiltersToParent, direction, sentimentSnapshot, sentimentError, allCurrencies, viewMode }) {

  const getDirectionButton = () => {
    const variant =
        direction === "NEUTRAL"
            ? "info"
            : direction === "LONG"
                ? "success" : "danger";
    const latestVerdict = String(sentimentSnapshot?.report?.verdict || '').toUpperCase();
    const { regimes: hourlyRegimes, missingHours } = sentimentSnapshot?.stale || !HOURLY_VERDICTS.has(latestVerdict)
        ? { regimes: [], missingHours: 0 }
        : getRecentHourlyRegimes([sentimentSnapshot.report, ...(sentimentSnapshot.history || [])]);
    const weekly = sentimentSnapshot?.outlooks?.find(outlook => outlook.horizonDays === 7);
    const weeklyVerdict = String(weekly?.verdict || '').toUpperCase();
    const weeklyAvailable = HOURLY_VERDICTS.has(weeklyVerdict);
    const hourlyLabel = hourlyRegimes.length > 0
        ? hourlyRegimes.map((regime, index) => (
            <React.Fragment key={`${regime.verdict}-${index}`}>
              {index > 0 && <span className="direction-hourly-arrow">→</span>}
              <HourlyRegime regime={regime}/>
            </React.Fragment>
        ))
        : <span className="direction-hourly-unavailable">Unavailable</span>;
    return (
        <Alert key={variant} variant={variant} className="direction-summary">
          <div className="direction-summary-grid">
            <span className="direction-summary-label">Right now</span>
            <strong className="direction-current-value">{direction}</strong>
            <span className="direction-summary-label">6–24h outlook</span>
            <span
                className="direction-hourly-value"
                title={sentimentSnapshot?.stale
                    ? "Hourly market research is overdue"
                    : sentimentError ? "Hourly market research could not be refreshed" : undefined}
            >
              {hourlyLabel}
            </span>
            {missingHours > 0 && <>
              <span />
              <span className="direction-history-gap">{missingHours} missing hourly report{missingHours === 1 ? '' : 's'}; durations count reports</span>
            </>}
            <span className="direction-summary-label">7-day outlook</span>
            <span className="direction-hourly-value" title={weekly?.asOf ? `Independent weekly forecast, reported ${weekly.asOf}` : undefined}>
              <span className={weeklyAvailable ? `direction-hourly-${weeklyVerdict.toLowerCase()}` : 'direction-hourly-unavailable'}>
                {weeklyAvailable ? verdictLabel(weeklyVerdict) : 'Unavailable'}
              </span>
              {weekly?.stale && weeklyAvailable && <span className="direction-hourly-unavailable">· overdue</span>}
            </span>
          </div>
        </Alert>
    )
  };

  return (
      <header>
        <div className="row w-100">
          <div style={wrapHeader} className="col-md-4">
            <div style={{marginLeft: '15px'}}>
              {/*<h2 className="brandColor">Insights app</h2>*/}
              <div className="last-updated" onClick={handleRefresh}>
                {lastUpdatedTs && <p>Last updated: {timeAgo(lastUpdatedTs)}</p>}
                <button id="refreshButton"></button>
              </div>
            </div>
          </div>

          {(viewMode === "insights" || viewMode === "setups" || viewMode === "history")
            ? <FilterForm sendFiltersToParent={sendFiltersToParent} allCurrencies={allCurrencies}
                ariaLabel={viewMode === "setups" ? "Setups filters"
                  : viewMode === "history" ? "History filters" : "Insights filters"}/>
            : <div className="px-lg-3 pb-2 col-md-4" />}

          <div className="px-lg-3 col-md-1">
          </div>

          <div className="px-lg-3 col-md-3">
            {getDirectionButton()}
            <div className="row w-100">
              <div className="col-md-12">
                <OpenCurrency formClass="pt-2 pb-2"/>
              </div>
            </div>
          </div>
        </div>
      </header>
  );
}

export default TopSection;
